import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  KoobBadge,
  KoobButton,
  KoobFeatureAccess,
  useDocumentTitle,
} from '@koob/ui';
import { useApp, useError, useSnack } from 'hooks';
import { useAsync } from 'react-async';
import { getRequestAccess, sendRequestAccess } from 'api/toRequestAccess';
import { KoobStateEnum } from 'utils';
import background from 'images/trip-designer1.png';
import KoobLayout from '../../../components/Koob/KoobLayout';

export const Discover = () => {
  const { t } = useTranslation('tripDesigner', { keyPrefix: 'discover' });
  const { t: tSidebar } = useTranslation('mainSidebar');
  useDocumentTitle(tSidebar('tripDesigner'));
  const { currentOrganizationId, user } = useApp();
  const { sendErrorSnack } = useError();
  const { notify } = useSnack();
  const [requesting, setRequesting] = useState(false);

  const { data, reload } = useAsync({
    promiseFn: getRequestAccess,
  });

  const requestAccess = data ? data?.data : {};
  // KD-3723: a validated organization has nothing to request here.
  if (requestAccess?.tripDesignerRequestState === KoobStateEnum.VALIDATED) {
    return <Navigate to="/trip-designer/trips" replace />;
  }
  const requestState = requestAccess?.tripDesignerRequestState;
  const isRequested = requestState
    ? requestState !== KoobStateEnum.NOT_REQUESTED
    : false;
  const isRefused = requestState === KoobStateEnum.REFUSED;

  const handleRequest = async () => {
    setRequesting(true);
    try {
      await sendRequestAccess(currentOrganizationId);
      notify(t('success'));
      reload();
    } catch (err) {
      sendErrorSnack(err);
    } finally {
      setRequesting(false);
    }
  };

  const action = (() => {
    if (!data) {
      return null;
    }
    if (isRequested) {
      return (
        <div className="flex flex-col items-start gap-2">
          <KoobBadge color={isRefused ? 'red' : 'blue'}>
            <i
              className={`fa-regular ${isRefused ? 'fa-circle-xmark' : 'fa-clock'}`}
              aria-hidden="true"
            />
            {t(isRefused ? 'requestRefused' : 'requestPending')}
          </KoobBadge>
          <p className="m-0 text-sm text-k-p">
            {t(
              isRefused
                ? 'requestRefusedDescription'
                : 'requestPendingDescription',
            )}
          </p>
        </div>
      );
    }
    if (user?.role !== 'to_admin') {
      return <p className="m-0 text-sm text-k-p">{t('adminOnly')}</p>;
    }
    return (
      <KoobButton
        variant="primary"
        size="lg"
        loading={requesting}
        onClick={handleRequest}
      >
        {t('requestAccess')}
      </KoobButton>
    );
  })();

  return (
    <KoobLayout>
      <KoobFeatureAccess
        eyebrow={tSidebar('tripDesigner')}
        title={t('title')}
        description={t('description')}
        features={[1, 2, 3, 4, 5, 6].map(itemNumber =>
          t(`textItem${itemNumber}`),
        )}
        image={background}
        action={action}
      />
    </KoobLayout>
  );
};
